import type { User as FirebaseUser } from 'firebase/auth';
import { storage } from '../utils/storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { logger } from '../utils/logger';
import type { StoredBillingAuth } from '../types/billing';

/**
 * UI-side half of the Stripe billing auth bridge (see premiumSyncStripe.ts
 * for the background half). Firebase is initialised only from UI surfaces, so
 * whenever auth state changes the UI mirrors the credentials the background
 * needs for its Firestore REST reads into chrome.storage. Called from
 * AuthContext when STRIPE_BILLING_ENABLED.
 */
export async function pushBillingAuth(user: FirebaseUser | null): Promise<void> {
  try {
    if (!user) {
      await storage.remove(STORAGE_KEYS.BILLING_AUTH);
      return;
    }
    const tokenResult = await user.getIdTokenResult();
    const value: StoredBillingAuth = {
      uid: user.uid,
      email: user.email ?? undefined,
      idToken: tokenResult.token,
      refreshToken: user.refreshToken || undefined,
      expiresAt: Date.parse(tokenResult.expirationTime),
    };
    await storage.set(STORAGE_KEYS.BILLING_AUTH, value);
  } catch (error) {
    logger.error(
      'Failed to push billing auth to storage',
      error instanceof Error ? error : new Error(String(error)),
      { module: 'billingAuth', function: 'pushBillingAuth' }
    );
  }
}
